import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FireIcon, MapPinIcon, BoltIcon } from '@heroicons/react/24/outline';
import { getAbsoluteImageUrl } from '../../lib/imageUtils';

interface Ad {
  id: string;
  title: string;
  slug: string;
  price: string;
  priceType: string;
  primaryImage: string | null;
  location: string;
  city: string;
  condition: string;
  publishedAt: string;
  isFeatured?: boolean;
  isTrending?: boolean;
  user: {
    id: string;
    username: string;
    displayName: string;
  };
}

interface TrendingSectionProps {
  ads: Ad[];
  title?: string;
}

const TrendingSection: React.FC<TrendingSectionProps> = ({
  ads,
  title = 'Trending',
}) => {
  const navigate = useNavigate();

  const formatPrice = (price: string | null) => {
    if (!price) return 'Free';
    const numPrice = parseFloat(price);
    if (!isNaN(numPrice)) {
      return Math.round(numPrice).toLocaleString('en-NP');
    }
    return price;
  };

  return (
    <div className="px-3 py-4" style={{ background: 'var(--pw-bg)' }}>
      {/* Section Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'rgba(232, 93, 76, 0.1)',
              color: 'var(--pw-primary)',
            }}
          >
            <FireIcon style={{ width: 20, height: 20 }} />
          </div>
          <h3 className="fw-bold mb-0" style={{ fontSize: 18, color: 'var(--pw-text)' }}>
            {title}
          </h3>
        </div>
      </div>

      {/* Grid */}
      {ads.length === 0 ? (
        <div className="text-center py-4">
          <p className="mb-0" style={{ fontSize: 14, color: 'var(--pw-text-muted)' }}>
            No {title.toLowerCase()} ads at the moment
          </p>
        </div>
      ) : (
        <div
          className="d-flex gap-3 overflow-auto pb-2"
          style={{ marginLeft: -12, marginRight: -12, paddingLeft: 12, paddingRight: 12 }}
        >
          {ads.map((ad, index) => (
            <motion.div
              key={ad.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => navigate(`/ad/${ad.slug}`)}
              className="flex-shrink-0 cursor-pointer"
              style={{
                width: 170,
                background: 'var(--pw-surface)',
                border: '1px solid var(--pw-border)',
                borderRadius: 0,
                overflow: 'hidden',
                boxShadow: '0 4px 14px rgba(116, 67, 35, 0.05)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 10px 24px rgba(116, 67, 35, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(116, 67, 35, 0.05)';
              }}
            >
              {/* Image */}
              <div className="position-relative bg-light" style={{ aspectRatio: '1/1', overflow: 'hidden' }}>
                {ad.primaryImage ? (
                  <img
                    src={getAbsoluteImageUrl(ad.primaryImage) || undefined}
                    alt={ad.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: '100%',
                      height: '100%',
                      background: 'var(--pw-bg-soft)',
                    }}
                  >
                    <svg
                      style={{ width: 40, height: 40, color: 'var(--pw-text-muted)' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}

                {/* Trending Badge */}
                {ad.isTrending && (
                  <div
                    className="position-absolute d-flex align-items-center gap-1"
                    style={{
                      top: 8,
                      left: 8,
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: 'var(--pw-success)',
                      color: 'white',
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    <FireIcon style={{ width: 12, height: 12 }} />
                    Hot
                  </div>
                )}

                {/* Sponsored Badge */}
                {ad.isFeatured && (
                  <div
                    className="position-absolute d-flex align-items-center gap-1"
                    style={{
                      top: 8,
                      right: 8,
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-strong) 100%)',
                      color: 'white',
                      fontSize: 10,
                      fontWeight: 700,
                      boxShadow: '0 4px 10px rgba(232, 93, 76, 0.3)',
                    }}
                  >
                    <BoltIcon style={{ width: 10, height: 10 }} />
                    Ad
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-3">
                <h4
                  className="fw-semibold mb-2"
                  style={{
                    fontSize: 14,
                    color: 'var(--pw-text)',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.4,
                    minHeight: 40,
                  }}
                >
                  {ad.title}
                </h4>

                <div className="d-flex align-items-baseline gap-1 mb-2">
                  <span className="fw-bold" style={{ fontSize: 16, color: 'var(--pw-primary-strong)' }}>
                    Rs. {formatPrice(ad.price)}
                  </span>
                  {ad.priceType === 'negotiable' && (
                    <span style={{ fontSize: 11, color: 'var(--pw-text-muted)' }}>(Neg)</span>
                  )}
                </div>

                <div
                  className="d-flex align-items-center gap-1"
                  style={{ fontSize: 12, color: 'var(--pw-text-muted)' }}
                >
                  <MapPinIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
                  <span className="text-truncate">{ad.city}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrendingSection;
